// engine-exception: "who has the phone tonight" is one aggregate read across every rota plus the pages waiting to be answered — a decision surface, not a resource list; and answering a page from it is the whole point
import { Link } from 'react-router-dom';
import { useAction, useApiQuery } from '../../engine/api/hooks';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Icon } from '../../engine/design/icons';

// On-call (docs/servicedesk-v2-proposal.md P11). Two questions, one screen: who is reachable right
// now, and is anybody being woken. A rota with nobody in it says so in plain words — an empty rota
// that looks fine is exactly how a P1 goes unanswered until morning.

interface Answer {
  schedule_id: string;
  schedule_name: string;
  technician_id: string | null;
  technician_name: string | null;
  reason?: string;
}
interface Page {
  id: string;
  ticket_id: string;
  tier_seq: number;
  round: number;
  channel: string;
  state: string;
  sent_at: string;
  ack_deadline: string;
}

const when = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
const left = (iso: string) => {
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  return mins <= 0 ? 'overdue' : `${mins} min left`;
};

export function OnCallScreen() {
  const who = useApiQuery<{ data: Answer[] }>(['on-call-who'], '/v1/on-call-schedules/who', { staleTime: 30_000 });
  // Zero stale time: this is the list you look at because your phone just buzzed.
  const mine = useApiQuery<{ data: Page[] }>(['pages-mine'], '/v1/pages/mine', { staleTime: 0 });
  const ack = useAction('pages', 'ack', { toast: { success: 'You have got it' } });

  if (who.error) return <ErrorBanner error={who.error} />;
  if (!who.data) return <Skeleton lines={6} />;

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      <PageHeader title="On call" subtitle="Who is reachable now, and what is waiting to be answered." />

      {(mine.data?.data.length ?? 0) > 0 && (
        <section className="hf-card" style={{ padding: 'var(--s-2)', borderColor: 'var(--neg)' }} aria-label="Waiting on you">
          <h2 className="hf-section-title">Waiting on you</h2>
          <ul className="hf-stack" style={{ gap: 'var(--s-1)', listStyle: 'none', padding: 0, margin: 0 }}>
            {mine.data!.data.map((p) => (
              <li key={p.id} className="hf-row" style={{ gap: 'var(--s-1)', alignItems: 'center' }}>
                <Badge tone={new Date(p.ack_deadline) < new Date() ? 'neg' : 'warn'}>{left(p.ack_deadline)}</Badge>
                <Link className="hf-link" to={`/tickets/${p.ticket_id}`}>Open the ticket</Link>
                <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>sent {when(p.sent_at)} · step {p.tier_seq}</span>
                <Button onClick={() => { void ack.mutateAsync({ id: p.id, body: {} }).then(() => mine.refetch()); }} disabled={ack.isPending}>
                  <Icon name="check" /> I have got it
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="Rotas">
        <h2 className="hf-section-title">Rotas</h2>
        {who.data.data.length === 0 ? (
          <EmptyState title="No rotas yet" hint="A rota decides whose phone rings out of hours." icon="bell" />
        ) : (
          <ul className="hf-stack" style={{ gap: 'var(--s-1)', listStyle: 'none', padding: 0, margin: 0 }}>
            {who.data.data.map((a) => (
              <li key={a.schedule_id} className="hf-row" style={{ gap: 'var(--s-1)', alignItems: 'baseline' }}>
                <strong>{a.schedule_name}</strong>
                {a.technician_name
                  ? <Badge tone="pos">{a.technician_name}</Badge>
                  // Not a blank: the reason is the difference between "quiet" and "nobody is covering".
                  : <Badge tone={a.reason === 'inside business hours' ? 'muted' : 'neg'}>{a.reason ?? 'nobody'}</Badge>}
                <Link className="hf-link" to={`/on-call-schedules/${a.schedule_id}`}>the rota</Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
